import React, { useState, useEffect, useRef } from 'react';
import {
    Download, TrendingUp, TrendingDown,
    BarChart2, AlertTriangle, Loader2
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer
} from 'recharts';
import { useLazyGetDomainReportQuery } from '@/lib/services/reportsApi';

const Reports: React.FC = () => {
    const [searchInput, setSearchInput] = useState('');
    const [searchedDomain, setSearchedDomain] = useState('');
    const [activeTab, setActiveTab] = useState<'consumer' | 'corporate'>('consumer');

    // Dropdown / Cache State
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [triggerSearch, { data: reportResponse, isLoading, isFetching, isError, error }] = useLazyGetDomainReportQuery();

    // Load cache on mount
    useEffect(() => {
        const saved = localStorage.getItem('recentDomainSearches');
        if (saved) {
            try { setRecentSearches(JSON.parse(saved)); }
            catch (e) { console.error("Failed to parse recent searches"); }
        }
    }, []);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Save search efficiently
    const saveToRecentSearches = (domain: string) => {
        setRecentSearches(prev => {
            const filtered = prev.filter(d => d.toLowerCase() !== domain.toLowerCase());
            const updated = [domain, ...filtered].slice(0, 5);
            localStorage.setItem('recentDomainSearches', JSON.stringify(updated));
            return updated;
        });
    };

    const handleSearch = () => {
        if (!searchInput.trim()) return;
        setSearchedDomain(searchInput);
        saveToRecentSearches(searchInput);
        triggerSearch(searchInput);
        setIsDropdownOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleDownloadPDF = () => {
        setTimeout(() => {
            window.print();
        }, 100);
    };

    // --- DATA MAPPING ---
    const rawResponse = reportResponse as any;
    const rootData = rawResponse;
    const nestedResults = rawResponse?.results;
    const isSearching = isLoading || isFetching;

    const totalLeaks = rootData?.total_leaks_count?.value ?? rootData?.total_leaks_count ?? 0;
    const consumerLeaks = rootData?.consumer_leaks_count?.value ?? rootData?.consumer_leaks_count ?? 0;
    const corporateLeaks = rootData?.corporate_leaks_count?.value ?? rootData?.corporate_leaks_count ?? 0;
    const infectedIPs = rootData?.infected_ip_addresses_count?.value ?? rootData?.infected_ip_addresses_count ?? 0;
    const affectedComputers = rootData?.affected_computers_count?.value ?? rootData?.affected_computers_count ?? 0;
    const getPct = (metric: any) => typeof metric === 'object' ? metric?.pct_delta : undefined;

    const renderTrend = (pct_delta?: number) => {
        if (pct_delta === undefined || pct_delta === 0) return null;
        const isPositive = pct_delta > 0;
        return (
            <span className={`flex items-center text-[10px] font-bold tracking-wider ${isPositive ? 'text-[#FF3333]' : 'text-[#50A970]'}`}>
                {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {isPositive ? '+' : ''}{pct_delta}%
            </span>
        );
    };

    const formatChartData = (timeline: any[]) => {
        if (!timeline || timeline.length === 0) return [];
        const aggregated = timeline.reduce((acc: Record<string, number>, pt: any) => {
            const key = `${pt.Year}-${String(pt.Month).padStart(2, '0')}`;
            if (!acc[key]) acc[key] = 0;
            acc[key] += pt.Count || 0;
            return acc;
        }, {});

        return Object.entries(aggregated)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-[#0A0B0E] border border-[#50A970]/30 p-3 rounded-lg shadow-[0_0_15px_rgba(80,169,112,0.15)] font-mono">
                    <p className="text-slate-400 text-[10px] mb-1">{label}</p>
                    <p className="text-[#50A970] text-sm font-bold">{payload[0].value.toLocaleString()} Incidents</p>
                </div>
            );
        }
        return null;
    };

    const renderSearchBar = () => (
        <div className="flex items-center gap-3 w-full relative z-50 mb-8 print:hidden">
            {/* Interactive Dropdown */}
            <div className="relative min-w-[200px]" ref={dropdownRef}>
                <div
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="bg-[#0A0B0E] border border-[#1A1D24] text-slate-400 text-[13px] px-4 py-2.5 rounded-lg flex items-center cursor-pointer hover:border-slate-500 transition-colors w-full select-none"
                >
                    <span>Select a Domain</span>
                    <span className={`ml-auto opacity-50 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                </div>

                {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-xl overflow-hidden z-50">
                        {recentSearches.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-slate-500 italic text-center">
                                No recent searches
                            </div>
                        ) : (
                            <ul className="py-1">
                                {recentSearches.map((domain, idx) => (
                                    <li
                                        key={idx}
                                        onClick={() => {
                                            setSearchInput(domain);
                                            setIsDropdownOpen(false);
                                        }}
                                        className="px-4 py-2.5 text-[13px] text-slate-300 hover:bg-[#161A23] hover:text-[#50A970] cursor-pointer transition-colors border-b border-[#1A1D24]/50 last:border-0"
                                    >
                                        {domain}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <div className="flex-1 relative">
                <input
                    type="text"
                    placeholder="e.g. example.com"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full bg-[#0A0B0E] border border-[#1A1D24] text-sm text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#50A970] transition-colors placeholder-slate-600"
                />
            </div>
            <button onClick={handleSearch} disabled={isSearching} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-8 py-2.5 rounded-lg transition-colors text-[13px] disabled:opacity-50">
                Search
            </button>
            {searchedDomain && !isError && (
                <button onClick={handleDownloadPDF} disabled={isSearching || isError} className="bg-transparent border border-[#1A1D24] text-slate-300 hover:text-white hover:bg-[#161A23] font-bold px-6 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2 ml-auto disabled:opacity-50">
                    <Download className="w-4 h-4" /> Download Report
                </button>
            )}
        </div>
    );

    // --- WELCOME / EMPTY STATE ---
    if (!searchedDomain && !isSearching && !rootData && !isError) {
        return (
            <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative overflow-hidden">
                <div className="flex justify-between items-end mb-4 relative z-50">
                    <div>
                        <h1 className="text-[22px] font-bold text-white tracking-wide">Reports</h1>
                        <p className="text-[10px] mt-1 font-semibold uppercase tracking-widest text-[#4da760]">System_Status: SUCCESS</p>
                    </div>
                </div>

                {renderSearchBar()}

                <div className="absolute inset-0 top-[180px] z-40 flex items-center justify-center backdrop-blur-md bg-black/60">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_50px_rgba(0,0,0,0.8)] rounded-2xl w-full max-w-[500px] p-8 flex flex-col relative overflow-hidden">
                        <div className="absolute -top-12 -left-12 w-48 h-48 border border-white/5 rounded-full"></div>
                        <div className="absolute -top-6 -left-6 w-36 h-36 border border-white/5 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-24 h-24 border border-white/5 rounded-full"></div>

                        <div className="w-12 h-12 -top-2 -left-2 border border-[#1A1D24] bg-[#12151C] rounded-xl flex items-center justify-center mb-6 relative z-10 shadow-inner">
                            <BarChart2 className="w-5 h-5 text-slate-300" />
                        </div>

                        <h2 className="text-[20px] font-bold text-white mb-3 relative z-10">Welcome to dashboard</h2>
                        <p className="text-[12px] text-slate-400 font-mono leading-relaxed relative z-10">
                            Start by entering a domain or pick a Watchlist query. We'll surface exposures, infected assets, and trends across our intelligence sources.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-4 gap-6 opacity-30 pointer-events-none select-none">
                    <div className="h-24 bg-[#0A0B0E] rounded-xl border border-[#1A1D24]"></div>
                    <div className="h-24 bg-[#0A0B0E] rounded-xl border border-[#1A1D24]"></div>
                    <div className="h-24 bg-[#0A0B0E] rounded-xl border border-[#1A1D24]"></div>
                    <div className="h-24 bg-[#0A0B0E] rounded-xl border border-[#1A1D24]"></div>
                </div>
            </div>
        );
    }

    // --- POPULATED / ERROR STATE ---
    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative overflow-x-hidden print:p-0 print:bg-black">

            {/* GLOBAL RESPONSIVE SYNCHRONIZATION OVERLAY */}
            {isSearching && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[99999] flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300 pl-[70px] lg:pl-[240px] print:hidden">
                    <div className="p-6 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl flex flex-col items-center max-w-xs text-center shadow-2xl relative overflow-hidden">
                        <div className="absolute -top-10 -left-10 w-24 h-24 bg-[#50A970]/5 rounded-full pointer-events-none blur-xl"></div>
                        <Loader2 className="h-9 w-9 text-[#50A970] animate-spin mb-3.5" />
                        <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Generating Report</h3>
                        <p className="text-[10px] text-slate-500 font-mono mt-1.5 leading-relaxed">
                            Fetching domain asset leaks and cross-referencing timeline structures...
                        </p>
                    </div>
                </div>
            )}

            {/* App Header */}
            <div className="flex justify-between items-end mb-2 print:hidden">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Reports</h1>
                    <p className="text-[10px] mt-1 font-semibold uppercase tracking-widest text-[#50A970]">System_Status: Success</p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {new Date().toISOString().replace('T', ' ').substring(0, 19)} UTC</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            {renderSearchBar()}

            <div id="printable-report" className="w-full">

                {/* DEDICATED PDF REPORT HEADER */}
                <div className="hidden print:flex flex-col mb-10 pb-6 border-b border-[#1A1D24]">
                    <div className="flex justify-between items-start mb-6">
                        <img src="/image/logo-2.png" alt="ThreatInformant" className="h-10 w-auto" />
                        <div className="text-right">
                            <p className="text-xs text-slate-400 font-mono">Date: {new Date().toLocaleString()}</p>
                        </div>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-1">Domain Exposure Intelligence Report</h1>
                        <p className="text-sm text-[#50A970] font-mono tracking-widest mt-2">TARGET: {searchedDomain.toUpperCase()}</p>
                    </div>
                </div>

                {isError ? (
                    <div className="bg-[#12151C] border border-[#FF3333]/30 rounded-xl p-8 flex flex-col items-center justify-center text-center mt-4 print:hidden">
                        <AlertTriangle className="w-8 h-8 text-[#FF3333] mb-4" />
                        <h3 className="text-[#FF3333] font-bold mb-2 text-lg">Report Generation Failed</h3>
                        <p className="text-slate-400 text-sm max-w-md">{(error as any)?.data?.error || "Error aggregating data."}</p>
                    </div>
                ) : rootData && !isSearching ? (
                    <div className="flex flex-col gap-6 print:gap-8 animate-in fade-in duration-300">

                        {/* KPI GRID */}
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg print:shadow-none break-inside-avoid">
                            <div className="grid grid-cols-1 md:grid-cols-3 print:grid-cols-3">

                                <div className="flex flex-col pb-6 border-b border-[#1A1D24] md:border-r print:border-r md:pr-6 print:pr-6">
                                    <div className="text-[11px] text-slate-500 font-mono mb-2 tracking-wide">Total Leaks</div>
                                    <div className="flex items-end justify-between mt-auto">
                                        <div className="text-2xl font-bold text-white tracking-tight">{totalLeaks.toLocaleString()}</div>
                                        {renderTrend(getPct(rootData?.total_leaks_count))}
                                    </div>
                                </div>

                                <div className="flex flex-col py-6 md:py-0 print:py-0 md:pb-6 print:pb-6 border-b border-[#1A1D24] md:border-r print:border-r md:px-6 print:px-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="text-[11px] text-slate-500 font-mono tracking-wide">Consumer Leaks</div>
                                    </div>
                                    <div className="flex items-end justify-between mt-auto">
                                        <div className="text-2xl font-bold text-white tracking-tight">{consumerLeaks.toLocaleString()}</div>
                                        {renderTrend(getPct(rootData?.consumer_leaks_count))}
                                    </div>
                                </div>

                                <div className="flex flex-col py-6 md:py-0 print:py-0 md:pb-6 print:pb-6 border-b border-[#1A1D24] md:pl-6 print:pl-6">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="text-[11px] text-slate-500 font-mono tracking-wide">Corporate Leaks</div>
                                    </div>
                                    <div className="flex items-end justify-between mt-auto">
                                        <div className="text-2xl font-bold text-white tracking-tight">{corporateLeaks.toLocaleString()}</div>
                                        {renderTrend(getPct(rootData?.corporate_leaks_count))}
                                    </div>
                                </div>

                                <div className="flex flex-col py-6 md:py-0 print:py-0 md:pt-6 print:pt-6 border-b md:border-b-0 print:border-b-0 border-[#1A1D24] md:border-r print:border-r md:pr-6 print:pr-6">
                                    <div className="text-[11px] text-slate-500 font-mono mb-2 tracking-wide">Infected IP Addresses</div>
                                    <div className="flex items-end justify-between mt-auto">
                                        <div className="text-2xl font-bold text-white tracking-tight">{infectedIPs.toLocaleString()}</div>
                                        {renderTrend(getPct(rootData?.infected_ip_addresses_count))}
                                    </div>
                                </div>

                                <div className="flex flex-col py-6 md:py-0 print:py-0 md:pt-6 print:pt-6 border-b md:border-b-0 print:border-b-0 border-[#1A1D24] md:border-r print:border-r md:px-6 print:px-6">
                                    <div className="text-[11px] text-slate-500 font-mono mb-2 tracking-wide">Affected Computers</div>
                                    <div className="flex items-end justify-between mt-auto">
                                        <div className="text-2xl font-bold text-white tracking-tight">{affectedComputers.toLocaleString()}</div>
                                        {renderTrend(getPct(rootData?.affected_computers_count))}
                                    </div>
                                </div>

                                <div className="flex flex-col pt-6 md:pl-6 print:pl-6 justify-center">
                                    <div className="text-[11px] text-slate-500 font-mono mb-3 tracking-wide">Recent Exposure</div>
                                    <div className="flex flex-wrap gap-2 mt-auto">
                                        <div className="bg-[#161A23] border border-[#1A1D24] px-3 py-1.5 rounded text-[11px] text-slate-400 font-mono flex-shrink-0">
                                            Combo lists: <span className="text-white font-bold ml-1">{nestedResults?.recent_exposure?.combolists_count || 0}</span>
                                        </div>
                                        <div className="bg-[#161A23] border border-[#1A1D24] px-3 py-1.5 rounded text-[11px] text-slate-400 font-mono flex-shrink-0">
                                            Stealers: <span className="text-white font-bold ml-1">{nestedResults?.recent_exposure?.stealers_count || 0}</span>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* GLOWING AREA CHARTS ROW */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-1 gap-6 print:gap-8">

                            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg print:shadow-none h-[320px] print:h-[300px] flex flex-col break-inside-avoid">
                                <h3 className="text-[14px] font-bold text-white mb-6 tracking-wide">Consumer Incident Timeline</h3>
                                <div className="flex-1 min-h-0 relative -ml-4">
                                    <ResponsiveContainer width="99%" height="100%">
                                        <AreaChart data={formatChartData(nestedResults?.consumer_incident_timeline)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorConsumer" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#50A970" stopOpacity={0.4} />
                                                    <stop offset="95%" stopColor="#50A970" stopOpacity={0.0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1D24" vertical={false} />
                                            <XAxis dataKey="name" stroke="#475569" fontSize={9} tickLine={false} axisLine={false} dy={10} minTickGap={30} interval="preserveStartEnd" />
                                            <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} dx={-10} />
                                            <RechartsTooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="count" stroke="#50A970" strokeWidth={3} fillOpacity={1} fill="url(#colorConsumer)" isAnimationActive={false} style={{ filter: 'drop-shadow(0px 0px 8px rgba(80,169,112,0.4))' }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg print:shadow-none h-[320px] print:h-[300px] flex flex-col break-inside-avoid">
                                <h3 className="text-[14px] font-bold text-white mb-6 tracking-wide">Corporate Incident Timeline</h3>
                                <div className="flex-1 min-h-0 relative -ml-4">
                                    <ResponsiveContainer width="99%" height="100%">
                                        <AreaChart data={formatChartData(nestedResults?.corporate_incident_timeline)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="colorCorporate" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#50A970" stopOpacity={0.4} />
                                                    <stop offset="95%" stopColor="#50A970" stopOpacity={0.0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#1A1D24" vertical={false} />
                                            <XAxis dataKey="name" stroke="#475569" fontSize={9} tickLine={false} axisLine={false} dy={10} minTickGap={30} interval="preserveStartEnd" />
                                            <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} dx={-10} />
                                            <RechartsTooltip content={<CustomTooltip />} />
                                            <Area type="monotone" dataKey="count" stroke="#50A970" strokeWidth={3} fillOpacity={1} fill="url(#colorCorporate)" isAnimationActive={false} style={{ filter: 'drop-shadow(0px 0px 8px rgba(80,169,112,0.4))' }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                        </div>

                        {/* LATEST EVENTS TABLE */}
                        <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg print:shadow-none break-inside-avoid mt-2">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-[14px] font-bold text-white tracking-wide">
                                    Latest {activeTab === 'consumer' ? 'Consumer' : 'Corporate'} Events
                                </h3>

                                <div className="flex bg-[#12151C] border border-[#1A1D24] rounded-lg overflow-hidden p-1 print:hidden">
                                    <button onClick={() => setActiveTab('consumer')} className={`px-4 py-1.5 text-[11px] font-bold transition-colors rounded ${activeTab === 'consumer' ? 'bg-[#50A970] text-black' : 'text-slate-400 hover:text-white'}`}>Consumer</button>
                                    <button onClick={() => setActiveTab('corporate')} className={`px-4 py-1.5 text-[11px] font-bold transition-colors rounded ${activeTab === 'corporate' ? 'bg-[#50A970] text-black' : 'text-slate-400 hover:text-white'}`}>Corporate</button>
                                </div>
                            </div>

                            <div className="overflow-x-auto custom-scrollbar print:overflow-visible">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-[#1A1D24] text-[10px] text-slate-500 uppercase tracking-widest">
                                            <th className="pb-3 px-4 font-semibold">Application</th>
                                            <th className="pb-3 px-4 font-semibold">Type</th>
                                            <th className="pb-3 px-4 font-semibold">Date</th>
                                            <th className="pb-3 px-4 font-semibold text-right">Records</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const eventData = activeTab === 'consumer' ? nestedResults?.latest_consumer_events : nestedResults?.latest_corporate_events;

                                            if (!eventData || eventData.length === 0) {
                                                return <tr><td colSpan={4} className="py-8 text-center text-[12px] text-slate-500 italic">No events recorded.</td></tr>;
                                            }

                                            return eventData.map((event: any, idx: number) => (
                                                <tr key={idx} className="border-b border-[#1A1D24]/50 hover:bg-[#161A23] transition-colors">
                                                    <td className="py-4 px-4 text-[12px] text-slate-300">{event.application || 'Unknown'}</td>
                                                    <td className="py-4 px-4 text-[12px] text-slate-400 capitalize">{event.type}</td>
                                                    <td className="py-4 px-4 text-[12px] text-slate-500">{event.date}</td>
                                                    <td className="py-4 px-4 text-[12px] text-white font-bold text-right">+{event.records?.toLocaleString() || 1}</td>
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>
                ) : null}
            </div>

            {/* --- THE NUCLEAR PRINT CSS --- */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
                
                @media print {
                    @page { 
                        size: A4 portrait; 
                        margin: 15mm 0mm; 
                    }
                    
                    aside, nav, header { display: none !important; }
                    
                    html, body, #root, main,
                    [class*="min-h-screen"], [class*="h-screen"], 
                    [class*="overflow-hidden"], [class*="overflow-y-auto"] {
                        height: auto !important;
                        min-height: auto !important;
                        max-height: none !important;
                        overflow: visible !important;
                        position: static !important;
                    }

                    body, #root, #root > div, main {
                        display: block !important;
                    }
                    
                    #printable-report {
                        display: block !important;
                        width: 100% !important;
                        background-color: #000000 !important;
                        color: #ffffff !important;
                        box-sizing: border-box !important;
                        padding-left: 25mm !important; 
                        padding-right: 25mm !important; 
                    }
                    
                    * {
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                        color-adjust: exact !important;
                    }

                    body { background-color: #000000 !important; }
                    
                    .break-inside-avoid { 
                        page-break-inside: avoid !important; 
                        break-inside: avoid !important; 
                        margin-bottom: 24px !important;
                        display: block !important;
                    }
                }
            `}} />
        </div>
    );
};

export default Reports;